import { Badge } from "@/components/Badge.tsx";
import { ClipBoardTooltip } from "@/components/ClipBoardTooltip.tsx";
import { ContentPane } from "@/components/ContentPane.tsx";
import { LongDate } from "@/components/l10n/DateFormat.tsx";
import { ActionModal, DeleteModal } from "@/components/modal/DeleteModal.tsx";
import { NavigateAfterDelete } from "@/components/NavigateAfterDelete.tsx";
import { PriceWithDiscount } from "@/components/PriceWithDiscount.tsx";
import { ResourceWithIcon } from "@/components/ResourceWithIcon.tsx";
import { DetailsHolder, DetailsTable } from "@/components/table/DetailsTable.tsx";
import { DetailsTableBillingAccount } from "@/components/table/DetailsTableBillingAccount.tsx";
import { MonthlyCostRow } from "@/components/table/DetailsTableMonthlyCost.tsx";
import { DetailsTableName } from "@/components/table/DetailsTableName.tsx";
import { DetailsTableRow } from "@/components/table/DetailsTableRow.tsx";
import { ViewTitle } from "@/components/Title.tsx";
import { Toolbar } from "@/components/Toolbar.tsx";
import { getResourceById } from "@/modules/api/resourceTypeException.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { LocationDetail } from "@/modules/location/LocationDetail.tsx";
import { AssignToSelect } from "@/modules/network/AssignToSelect.tsx";
import {
    assignIpAddressMutation,
    deleteIpAddressMutation,
    modifyIpAddressMutation,
    unassignIpAddressMutation,
    type IpAddressWithType,
} from "@/modules/network/ipAddress/ipAddressApi.ts";
import { assignedIpsQueryAtom } from "@/modules/network/ipAddress/joinAssignedQuery.ts";
import {
    UNASSIGNED_RESOURCE,
    getAssignBodyFromResource,
    getAssignedResourceId,
    resourceIdToString,
    type IpAssignableResource,
} from "@/modules/network/ipAddress/resourceId.ts";
import { UnAssignButton } from "@/modules/network/ipAddress/UnAssignButton.tsx";
import { pricesAtom } from "@/modules/pricing/pricingQuery.ts";
import { getIpAddressPrices } from "@/modules/pricing/resourcePricing.ts";
import { useDeletableResource } from "@/utils/query/useDeletableResource.tsx";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import type { IpAddressModifyBody } from "@warrenio/api-spec/spec.oats.gen";

function UnassignContent({ item }: { item: IpAddressWithType }) {
    const prices = useSuspenseQueryAtom(pricesAtom);
    const unassignedIpPrice = getIpAddressPrices(prices, item.location).unassigned;
    return (
        <>
            <p>
                <Trans>
                    Removing the public IPv4 address assignment from this resource will make the resource inaccessible
                    from the public Internet. The IP address will remain reserved to your account. You can manage public
                    IPv4 addresses from the Network menu on the left sidebar.
                </Trans>
            </p>
            <p className="mt-2">
                <Trans>
                    When reserved but not assigned to a resource IPv4 address will be charged for{" "}
                    <PriceWithDiscount
                        price={unassignedIpPrice}
                        billingAccountId={item.billing_account_id}
                        allowFree={false}
                    />{" "}
                    / month.
                </Trans>
            </p>
        </>
    );
}

export function IpAddressView({ location, addressId }: { location: string; addressId: string }) {
    //#region Hooks
    const { t } = useLingui();
    const modifyMutation = useStandardMutation(modifyIpAddressMutation);
    const assignMutation = useStandardMutation(assignIpAddressMutation);
    const unassignMutation = useStandardMutation(unassignIpAddressMutation);
    const deleteMutation = useStandardMutation(deleteIpAddressMutation);

    const data = useSuspenseQueryAtom(assignedIpsQueryAtom(location));

    const obj = useDeletableResource(() => getResourceById(data, addressId, "ip_address"), deleteMutation);
    //#endregion

    if (obj === undefined) {
        return <NavigateAfterDelete />;
    }

    const { $type, address, billing_account_id, created_at, name } = obj;
    const assignedResourceId = getAssignedResourceId(obj);

    async function onDelete() {
        await deleteMutation.mutateAsync({
            location,
            address,
        });
    }

    async function onModify(body: IpAddressModifyBody) {
        await modifyMutation.mutateAsync({
            body,
            location,
            address,
        });
    }

    async function unAssign() {
        await unassignMutation.mutateAsync({
            location,
            address,
        });
    }

    async function assignTo(value: IpAssignableResource | UNASSIGNED_RESOURCE) {
        if (value === UNASSIGNED_RESOURCE) {
            await unAssign();
        } else {
            await assignMutation.mutateAsync({
                body: getAssignBodyFromResource(value),
                location,
                address,
            });
        }
    }

    return (
        <>
            <Toolbar>
                <ActionModal
                    buttonComponent={UnAssignButton}
                    label={t`Unassign`}
                    title={t`Unassign`}
                    isDisabled={!assignedResourceId}
                    // TODO: disable show modal/button hint when isDisabled?
                    modalAction={async () => await unAssign()}
                >
                    <UnassignContent item={obj} />
                </ActionModal>
                <DeleteModal modalAction={onDelete}>
                    <Trans>
                        Deleting the floating IP address "{name || address}" will remove it from your account and
                        release it to the public pool.
                    </Trans>
                </DeleteModal>
            </Toolbar>

            <ContentPane className="VStack gap-4">
                <ViewTitle title={name ? name : address} />

                <DetailsHolder>
                    <DetailsTable>
                        <DetailsTableName value={name} onChange={async (e) => await onModify({ name: e })} />
                        <DetailsTableRow title={t`Type`}>
                            <ResourceWithIcon type={$type} />
                        </DetailsTableRow>
                        <DetailsTableRow title={t`Public IPv4`}>
                            <ClipBoardTooltip>{address}</ClipBoardTooltip>
                        </DetailsTableRow>
                        <DetailsTableRow title={t`Status`}>
                            {assignedResourceId !== UNASSIGNED_RESOURCE ? (
                                <Badge color="success">
                                    <Trans>Assigned</Trans>
                                </Badge>
                            ) : (
                                <Badge color="error">
                                    <Trans>Unassigned</Trans>
                                </Badge>
                            )}
                        </DetailsTableRow>
                        <DetailsTableRow title={t`Assigned to`}>
                            <AssignToSelect
                                enableInlineEditing
                                valueKey={resourceIdToString(assignedResourceId)}
                                location={location}
                                onChange={async (obj) => await assignTo(obj)}
                            />
                        </DetailsTableRow>
                    </DetailsTable>
                    <DetailsTable>
                        <DetailsTableRow title={t`Created`}>
                            <LongDate date={created_at} />
                        </DetailsTableRow>
                        <LocationDetail slug={location} />
                        <DetailsTableRow />
                        <DetailsTableBillingAccount
                            valueKey={billing_account_id}
                            onChange={async (item) => await onModify({ billing_account_id: item.id })}
                        />
                        <MonthlyCostRow price={obj.price} billingAccountId={billing_account_id} />
                    </DetailsTable>
                </DetailsHolder>
            </ContentPane>
        </>
    );
}
